import { Stack, Box, Button, styled, Tooltip, Divider } from "@mui/material";
import { getAttributionTitle } from "Helpers/getAttributionTitle";
import produce from "immer";
import { enumToArray } from "Helpers/enumToText";
import { ApiAttributionModel } from "@incendium/api";
import AnalyticsAttributionIcon from "./AnalyticsAttributionIcon";
import { CallbackOrVal } from "Interfaces";
import { useHover } from "react-use";
import { MoreHoriz } from "@mui/icons-material";

interface IAnalyticsAttributionSwitcherProps {
  models: (ApiAttributionModel | null)[];
  setModels: (v: CallbackOrVal<ApiAttributionModel[]>) => void;
  multiple?: boolean;
  withBg?: boolean;
  collapsible?: boolean;
  twoRows?: boolean; // <-- Add this prop
  fullWidth?: boolean;
}

const StyledContainer = styled(Box)<{
  withBg?: boolean;
  expanded?: boolean;
  collapsible?: boolean;
  fullWidth?: boolean;
}>(({ theme, withBg, expanded, collapsible, fullWidth }) => ({
  background: withBg
    ? `rgba(255, 255, 255, 0.1)`
    : theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  padding: "5px 6px",
  maxWidth: "100%",
  width: fullWidth ? "100%" : collapsible ? (expanded ? 365 + 90 : 90) : 365,
  overflow: "hidden",
  transition: "all 0.2s ease-in-out",
  willChange: ["width"],
  border: withBg
    ? expanded
      ? `1px solid ${theme.palette.primary.main}`
      : `1px solid ${theme.palette.background.light}`
    : undefined,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: "transparent",
  padding: `0`,
  borderRadius: 14,
  minWidth: 28,
  maxWidth: 40,
  height: 24,

  "&.active": {
    background: theme.palette.primary.main,
    "& svg": {
      fill: theme.palette.common.white,
    },
    "& .MuiTypography-root": {
      color: theme.palette.common.white,
    },
  },

  "& svg, & .MuiTypography-root": {
    height: "100%",
    fill: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  "&:hover svg, ,&:hover  .MuiTypography-root": {
    transition: "all 1.2s",
    fill: theme.palette.common.white,
    color: theme.palette.common.white,
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.primary.main,
  maxWidth: 10,
}));

const StyledSelectedBox = styled(Box)(({ theme }) => ({
  minWidth: 40,
  maxWidth: "100%",
  width: 40,
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: 2,
  color: theme.palette.primary.main,
  "& svg": {
    fill: theme.palette.primary.main,
  },
}));

const StyledMoreIcon = styled(MoreHoriz)(() => ({
  marginLeft: "4px!important",
}));

function AnalyticsAttributionSwitcher({
  models,
  setModels,
  multiple,
  withBg,
  collapsible,
  twoRows,
  fullWidth,
}: IAnalyticsAttributionSwitcherProps) {
  // Helper to chunk array into arrays of length n
  const chunkArray = (arr: any[], size: number) =>
    arr.length > size ? [arr.slice(0, size), arr.slice(size)] : [arr];

  const attributionModels = enumToArray(ApiAttributionModel).filter(
    (m) => m !== ApiAttributionModel.NO_MODEL_SELECTED,
  );
  const buttonRows = twoRows
    ? chunkArray(attributionModels, 4)
    : [attributionModels];

  const renderButtons = () =>
    buttonRows.map((row, i) => (
      <Stack
        direction="row"
        justifyContent={"space-between"}
        p={twoRows ? 1 : 0}
        spacing={2}
        key={i}
      >
        {row.map((m) => (
          <Tooltip title={getAttributionTitle(m)} key={m}>
            <StyledButton
              sx={{
                minWidth: [
                  ApiAttributionModel.EBA,
                  ApiAttributionModel.CUSTOM,
                ].includes(m)
                  ? 40
                  : undefined,
              }}
              onClick={() => {
                if (multiple) {
                  setModels((models) =>
                    produce(models, (draft) => {
                      const idx = draft.indexOf(m);
                      if (idx < 0) {
                        draft.push(m);
                      } else if (draft.length > 1) {
                        draft.splice(idx, 1);
                      }
                    }),
                  );
                  return;
                }
                setModels([m]);
              }}
              className={models.includes(m) ? "active" : ""}
            >
              <AnalyticsAttributionIcon sx={{ width: "100%" }} model={m} />
            </StyledButton>
          </Tooltip>
        ))}
      </Stack>
    ));

  const renderContainer = (isHovered) => (
    <StyledContainer
      expanded={isHovered}
      withBg={withBg}
      collapsible={collapsible}
      fullWidth={fullWidth}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent={"space-between"}
      >
        {collapsible && (
          <Stack
            direction={"row"}
            alignItems="center"
            justifyContent={"flex-start"}
          >
            {models.map((m) => (
              <StyledSelectedBox key={m!}>
                <AnalyticsAttributionIcon sx={{ width: "100%" }} model={m!} />
              </StyledSelectedBox>
            ))}
            <StyledDivider orientation="vertical" variant="inset" flexItem />
          </Stack>
        )}

        {!collapsible || isHovered ? (
          <Box sx={{ width: twoRows ? "100%" : undefined }}>
            {twoRows ? renderButtons() : renderButtons()}
          </Box>
        ) : (
          <StyledMoreIcon color="primary" />
        )}
      </Stack>
    </StyledContainer>
  );
  const [hoverable] = useHover(renderContainer);
  return hoverable;
}

export default AnalyticsAttributionSwitcher;
